import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatedRoutes } from "./components/AnimatedRoutes";
import { PullToRefresh } from "./components/PullToRefresh";
import { DataProvider } from "./data/DataProvider";
import { SampleCartProvider } from "./contexts/SampleCartContext";
import { SampleCartDrawer, SampleCartButton } from "./components/SampleCartDrawer";
import { ProjectHubProvider } from "./contexts/ProjectHubContext";
import HomePage from "./pages/HomePage";
import { ChatWidget } from "./components/ChatWidget";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { ExitIntentPopup } from "./components/ExitIntentPopup";
import { CallbackPopup } from "./components/CallbackPopup";
import { AnalyticsProvider } from "./components/AnalyticsProvider";
import TrackingPixels from "./components/TrackingPixels";
import { QuickActions } from "./components/QuickActions";
import { BottomNav } from "./components/BottomNav";
import { InstallPrompt } from "./components/InstallPrompt";
import AutoSEO from "./components/AutoSEO";
import RedirectBoundary from "./components/RedirectBoundary";

// Lazy load all other pages for better performance
const ServicePage = lazy(() => import("./pages/ServicePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrastaraProductPage = lazy(() => import("./pages/PrastaraProductPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const ProjectDetailPage = lazy(() => import("./pages/ProjectDetailPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const CompanyProfilePage = lazy(() => import("./pages/CompanyProfilePage"));
const ProcessPage = lazy(() => import("./pages/ProcessPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const CapabilitiesPage = lazy(() => import("./pages/CapabilitiesPage"));
const CapabilityDetailPage = lazy(() => import("./pages/CapabilityDetailPage"));
const FinishesLibraryPage = lazy(() => import("./pages/FinishesLibraryPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const CaseStudyDetailPage = lazy(() => import("./pages/CaseStudyDetailPage"));
const DownloadsPage = lazy(() => import("./pages/DownloadsPage"));
const IndustriesPage = lazy(() => import("./pages/IndustriesPage"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const QualityHSEPage = lazy(() => import("./pages/QualityHSEPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const SolutionsPage = lazy(() => import("./pages/SolutionsPage"));
const SolutionDetailPage = lazy(() => import("./pages/SolutionDetailPage"));
const VideoGalleryPage = lazy(() => import("./pages/VideoGalleryPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const SustainabilityPage = lazy(() => import("./pages/SustainabilityPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const QuoteCalculatorPage = lazy(() => import("./pages/QuoteCalculatorPage"));
const RFQBuilderPage = lazy(() => import("./pages/RFQBuilderPage"));
const SampleKitPage = lazy(() => import("./pages/SampleKitPage"));
const BookingPage = lazy(() => import("./pages/BookingPage"));
const ResourceCenterPage = lazy(() => import("./pages/ResourceCenterPage"));
const CertificationsPage = lazy(() => import("./pages/CertificationsPage"));
const TechnicalKnowledgeBasePage = lazy(() => import("./pages/TechnicalKnowledgeBasePage"));
const CitationPage = lazy(() => import("./pages/CitationPage"));
const LocalServicePage = lazy(() => import("./pages/LocalServicePage"));
const CollaboratePage = lazy(() => import("./pages/CollaboratePage"));
const StudioWorksPage = lazy(() => import("./pages/StudioWorksPage"));
const StudioWorkDetailPage = lazy(() => import("./pages/StudioWorkDetailPage"));
const CollectionsPage = lazy(() => import("./pages/CollectionsPage"));
const CollectionDetailPage = lazy(() => import("./pages/CollectionDetailPage"));
const ArtAtelierPage = lazy(() => import("./pages/ArtAtelierPage"));
const ArtCategoryDetailPage = lazy(() => import("./pages/ArtCategoryDetailPage"));
const StudioHomePage = lazy(() => import("./pages/StudioHomePage"));
const MaterialInnovationPage = lazy(() => import("./pages/MaterialInnovationPage"));
const LabCapabilityDetailPage = lazy(() => import("./pages/LabCapabilityDetailPage"));
const KnowledgeCategoryPage = lazy(() => import("./pages/KnowledgeCategoryPage"));
const StudioCollaboratePage = lazy(() => import("./pages/StudioCollaboratePage"));
const GCCCountryPage = lazy(() => import("./pages/GCCCountryPage"));
const UAEServicePage = lazy(() => import("./pages/UAEServicePage"));
const AfricaMarketPage = lazy(() => import("./pages/AfricaMarketPage"));
const ThoughtLeadershipPage = lazy(() => import("./pages/ThoughtLeadershipPage"));
const MetalWorkTypePage = lazy(() => import("./pages/MetalWorkTypePage"));
const SubtypeDetailPage = lazy(() => import("./pages/SubtypeDetailPage"));
const StudioSubtypeDetailPage = lazy(() => import("./pages/StudioSubtypeDetailPage"));
const PrastaraPage = lazy(() => import("./pages/PrastaraPage"));
const ArtisanPage = lazy(() => import("./pages/ArtisanPage"));
const RALVisualizerPage = lazy(() => import("./pages/RALVisualizerPage"));
const ElectroplatingVisualizerPage = lazy(() => import("./pages/ElectroplatingVisualizerPage"));
const PantoneVisualizerPage = lazy(() => import("./pages/PantoneVisualizerPage"));
const DrawingUploadPage = lazy(() => import("./pages/DrawingUploadPage"));
const AreaCalculatorPage = lazy(() => import("./pages/AreaCalculatorPage"));
const SurfaceAreaCalculatorPage = lazy(() => import("./pages/SurfaceAreaCalculatorPage"));
const MetalWeightCalculatorPage = lazy(() => import("./pages/MetalWeightCalculatorPage"));
const FinishDurabilityGuidePage = lazy(() => import("./pages/FinishDurabilityGuidePage"));
const PanelConfiguratorPage = lazy(() => import("./pages/PanelConfiguratorPage"));
const PerforationDesignerPage = lazy(() => import("./pages/PerforationDesignerPage"));
const MaterialSelectorPage = lazy(() => import("./pages/MaterialSelectorPage"));
const FinishPairingPage = lazy(() => import("./pages/FinishPairingPage"));
const ProjectScopeBuilderPage = lazy(() => import("./pages/ProjectScopeBuilderPage"));
const GaugeGuidePage = lazy(() => import("./pages/GaugeGuidePage"));
const ProjectHubPage = lazy(() => import("./pages/ProjectHubPage"));
const MoodBoardPage = lazy(() => import("./pages/MoodBoardPage"));
const SharedProjectPage = lazy(() => import("./pages/SharedProjectPage"));
const ThreeDConfiguratorPage = lazy(() => import("./pages/ThreeDConfiguratorPage"));
const ARVisualizerPage = lazy(() => import("./pages/ARVisualizerPage"));
const BespokeToolsListingPage = lazy(() => import("./pages/BespokeToolsListingPage"));
const EstimationToolsListingPage = lazy(() => import("./pages/EstimationToolsListingPage"));

// Loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-obsidian flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-2 border-bronze border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-white/60 text-sm font-body uppercase tracking-wider">Loading...</p>
    </div>
  </div>
);

const ScrollReset = () => {
  const { pathname } = useLocation();
  useEffect(() => { 
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); 
  }, [pathname]);
  return null;
};

function App() {
  return (
    <DataProvider>
      <SampleCartProvider>
      <ProjectHubProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollReset />
          <RedirectBoundary />
          <AutoSEO />
          {/* StickyCTA removed — the existing <QuickActions /> provides sticky
              desktop right-rail CTAs (mail/doc/calendar/phone) and a mobile
              bottom nav with QUOTE + CONTACT already. Adding a second rail
              caused overlap and duplicate buttons. The real Phase 5 win is
              the per-page InlineInquiryForm embedded in service + Prastara
              product pages. See /app/memory/CHANGELOG.md 'Oct 7 — Phase 5'. */}
          <PullToRefresh />
          <Suspense fallback={<PageLoader />}>
            <AnimatedRoutes>
              <Route path="/" element={<HomePage />} />
              <Route path="/signage-wayfinding-dubai" element={<ServicePage />} />
              <Route path="/contact" element={<ContactPage />} />
              {/* Phase 3 money-keyword service pages — data-driven template */}
              <Route path="/pvd-coating-dubai" element={<ServicePage />} />
              <Route path="/pvdf-coating-dubai" element={<ServicePage />} />
              <Route path="/electrogalvanizing-dubai" element={<ServicePage />} />
              <Route path="/electroplating-dubai" element={<ServicePage />} />
              <Route path="/liquid-metal-coating-dubai" element={<ServicePage />} />
              <Route path="/custom-metal-furniture-dubai" element={<ServicePage />} />
              <Route path="/laser-cutting-dubai" element={<ServicePage />} />
              <Route path="/tube-forming-dubai" element={<ServicePage />} />
              <Route path="/metal-art-fabrication-dubai" element={<ServicePage />} />
              <Route path="/metal-exhibition-stands-dubai" element={<ServicePage />} />
              <Route path="/metal-wall-panels-dubai" element={<ServicePage />} />
              <Route path="/architectural-metal-facades-uae" element={<ServicePage />} />
              <Route path="/art-atelier" element={<ServicePage />} />
              <Route path="/service/:id" element={<ServiceDetailPage />} />
              <Route path="/services/:slug" element={<LocalServicePage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:slug" element={<ProjectDetailPage />} />
              <Route path="/company-profile" element={<CompanyProfilePage />} />
              <Route path="/process" element={<ProcessPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/capabilities" element={<CapabilitiesPage />} />
              <Route path="/capabilities/:slug" element={<CapabilityDetailPage />} />
              <Route path="/capabilities/:capabilitySlug/:subtypeSlug" element={<SubtypeDetailPage />} />
              <Route path="/capabilities/metal-work/:slug" element={<MetalWorkTypePage />} />
              <Route path="/finishes" element={<FinishesLibraryPage />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/case-study/:id" element={<CaseStudyDetailPage />} />
              <Route path="/downloads" element={<DownloadsPage />} />
              <Route path="/industries" element={<IndustriesPage />} />
              <Route path="/industries/:slug" element={<IndustryPage />} />
              <Route path="/quality-hse" element={<QualityHSEPage />} />
              <Route path="/insights" element={<BlogPage />} />
              <Route path="/insights/:slug" element={<BlogPostPage />} />
              <Route path="/solutions" element={<SolutionsPage />} />
              <Route path="/solutions/:slug" element={<SolutionDetailPage />} />
              <Route path="/videos" element={<VideoGalleryPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/sustainability" element={<SustainabilityPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/quote" element={<QuoteCalculatorPage />} />
              <Route path="/rfq" element={<RFQBuilderPage />} />
              <Route path="/samples" element={<SampleKitPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/resources" element={<ResourceCenterPage />} />
              <Route path="/certifications" element={<CertificationsPage />} />
              <Route path="/knowledge-base" element={<TechnicalKnowledgeBasePage />} />
              <Route path="/knowledge-base/:category" element={<KnowledgeCategoryPage />} />
              <Route path="/guides/:slug" element={<CitationPage />} />
              <Route path="/collaborate" element={<CollaboratePage />} />
              <Route path="/studio-collaborate" element={<StudioCollaboratePage />} />
              <Route path="/material-innovation" element={<MaterialInnovationPage />} />
              <Route path="/material-innovation/:slug" element={<LabCapabilityDetailPage />} />
              <Route path="/studio" element={<StudioHomePage />} />
              <Route path="/studio-works" element={<StudioWorksPage />} />
              <Route path="/studio-works/:slug" element={<StudioWorkDetailPage />} />
              <Route path="/studio-works/:studioSlug/:subtypeSlug" element={<StudioSubtypeDetailPage />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/collections/:slug" element={<CollectionDetailPage />} />
              <Route path="/art-atelier" element={<ArtAtelierPage />} />
              <Route path="/art-atelier/:slug" element={<ArtCategoryDetailPage />} />
              <Route path="/gcc/:country" element={<GCCCountryPage />} />
              <Route path="/uae/:serviceSlug" element={<UAEServicePage />} />
              <Route path="/africa/:countrySlug" element={<AfricaMarketPage />} />
              <Route path="/thought-leadership" element={<ThoughtLeadershipPage />} />
              <Route path="/prastara" element={<PrastaraPage />} />
              <Route path="/prastara/:categorySlug" element={<PrastaraPage />} />
              <Route path="/prastara/:categorySlug/:productSlug" element={<PrastaraProductPage />} />
              <Route path="/artisan" element={<ArtisanPage />} />
              <Route path="/artisan/:categorySlug" element={<ArtisanPage />} />
              <Route path="/color-visualizer" element={<RALVisualizerPage />} />
              <Route path="/electroplating-visualizer" element={<ElectroplatingVisualizerPage />} />
              <Route path="/pantone-visualizer" element={<PantoneVisualizerPage />} />
              <Route path="/upload-drawings" element={<DrawingUploadPage />} />
              <Route path="/area-calculator" element={<AreaCalculatorPage />} />
              <Route path="/calculators/area" element={<AreaCalculatorPage />} />
              <Route path="/surface-area-calculator" element={<SurfaceAreaCalculatorPage />} />
              <Route path="/calculators/surface-area" element={<SurfaceAreaCalculatorPage />} />
              <Route path="/metal-weight-calculator" element={<MetalWeightCalculatorPage />} />
              <Route path="/calculators/metal-weight" element={<MetalWeightCalculatorPage />} />
              <Route path="/finish-durability-guide" element={<FinishDurabilityGuidePage />} />
              <Route path="/tools" element={<BespokeToolsListingPage />} />
              <Route path="/tools/panel-configurator" element={<PanelConfiguratorPage />} />
              <Route path="/tools/perforation-designer" element={<PerforationDesignerPage />} />
              <Route path="/tools/material-selector" element={<MaterialSelectorPage />} />
              <Route path="/tools/finish-pairing" element={<FinishPairingPage />} />
              <Route path="/tools/scope-builder" element={<ProjectScopeBuilderPage />} />
              <Route path="/tools/gauge-guide" element={<GaugeGuidePage />} />
              <Route path="/calculators" element={<EstimationToolsListingPage />} />
              <Route path="/project-hub" element={<ProjectHubPage />} />
              <Route path="/project-hub/shared/:shareId" element={<SharedProjectPage />} />
              <Route path="/project-hub/:id" element={<MoodBoardPage />} />
              <Route path="/tools/3d-configurator" element={<ThreeDConfiguratorPage />} />
              <Route path="/tools/ar-visualizer" element={<ARVisualizerPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </AnimatedRoutes>
          </Suspense>
          <AnalyticsProvider>
            <TrackingPixels />
            <ChatWidget />
            <WhatsAppButton />
            <QuickActions />
            <ExitIntentPopup />
            <CallbackPopup />
          </AnalyticsProvider>
          <BottomNav />
          <InstallPrompt />
          <SampleCartDrawer />
          <SampleCartButton />
        </BrowserRouter>
      </div>
      </ProjectHubProvider>
      </SampleCartProvider>
    </DataProvider>
  );
}

export default App;
